import {zodResolver} from '@hookform/resolvers/zod';
import {Alert, Box, Button, Container, Paper, Stack} from '@mui/material';
import {RhfTextField} from 'mui-rhf-integration';
import {useState} from 'react';
import {useForm} from 'react-hook-form';
import {z} from 'zod';
import {useUserContext} from '@/components/Providers/UserContext';
import type {SignInResponse} from '@/types/SignInResponse';
import type {User} from '@/types/User';
import {errorMap} from '@/utils/errorMap';

const loginFormSchema = z.object({
    username: z.string(),
    password: z.string(),
});

export type StudentLoginFormSchemaType = z.infer<typeof loginFormSchema>;

const SignInForm = () : React.ReactElement => {
    const {authenticateUser, setUser} = useUserContext();
    const [loginError, setLoginError] = useState('');

    const form = useForm<StudentLoginFormSchemaType>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {},
        resolver: zodResolver(loginFormSchema, {errorMap}),
    });

    const handleSubmit = async () => {
        const formValues = form.getValues();
        setLoginError('');

        const response : Response = await authenticateUser(
            formValues.username,
            formValues.password
        );

        if (response.ok) {
            const signInResponse = await response.json() as SignInResponse;

            // no error code field returned !?!
            // if (signInResponse.errorCode !== 0) {
            //     setLoginError(signInResponse.message);
            //     return;
            // }

            const newUser : User = {
                username: formValues.username,
                jwt: signInResponse.jwt ?? '',
                displayName: signInResponse.displayName,
                employeeID: signInResponse.employeeID,
                isAdmin: signInResponse.isAdmin,
            };

            setUser(newUser);
            return;
        }

        setLoginError('Error: Login Unsuccessful');
    };

    return <Container maxWidth='sm' sx={{mt: 3}}>
        <Paper sx={{p: 3}}>
            <Box>
                <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
                    <Stack>
                        <RhfTextField
                            control={form.control}
                            name="username"
                            label="Username"
                            placeholder="Username"
                            sx={{my: 1}}
                            onChangeCapture={() => {
                                form.clearErrors();
                            }}
                        />
                        <RhfTextField
                            type='password'
                            control={form.control}
                            name="password"
                            label="Password"
                            placeholder="Password"
                            sx={{my: 1}}
                            onChangeCapture={() => {
                                form.clearErrors();
                            }}
                        />
                        {loginError && <Stack direction="row" justifyContent="space-between">
                            <Alert severity="error" sx={{width: '100%', my: 2}}>{loginError}</Alert>
                        </Stack>}
                        <Stack direction="row" justifyContent="flex-end">
                            <Button
                                variant="outlined"
                                type="submit"
                                color="primary"
                            >
                                Log In
                            </Button>
                        </Stack>
                    </Stack>
                </form>
            </Box>
        </Paper>
    </Container>;
};

export default SignInForm;
